import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { contabilidadeDigitalTranslations, createCdPaths } from '@ibk/contabilidade-digital';
import { useHomeProducts } from '@ibk/shared-config';
import { useGetHomePermissions } from '@ibk/shared-permissioning';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { ApiProductStatus, ProductAlias } from '@ibk/shared-types';
import { Loading, NavBar } from '@ibk/shared-ui';
import { ClientCenterHeaderRightWrapper, PersonalizedHeader, usePersonalizationTheme } from '../../components';
import { ClientCenterAccessGuard } from '../../guards/ClientCenterAccessGuard';
import { dominioempresaTranslations } from '../../translations';
import ClientCenterRouter, { ccPaths } from '../ClientCenter/router';
import ContabilidadeDigital from './ContabilidadeDigital';
import { useCndMenuAvailability } from './hooks/useCndMenuAvailability';
import styles from './Layout.module.css';
export default function ContabilidadeLayout() {
    const combinedTranslations = {
        'pt-BR': {
            ...dominioempresaTranslations['pt-BR'],
            ...contabilidadeDigitalTranslations['pt-BR'],
        },
        'en-US': {
            ...dominioempresaTranslations['en-US'],
            ...contabilidadeDigitalTranslations['en-US'],
        },
        'es-ES': {
            ...dominioempresaTranslations['es-ES'],
            ...contabilidadeDigitalTranslations['es-ES'],
        },
    };
    return (_jsx(TranslationProvider, { translations: combinedTranslations, defaultLanguage: 'pt-BR', children: _jsx(LayoutContent, {}) }));
}
function LayoutContent() {
    const { t } = useTranslation();
    const { hasTheme } = usePersonalizationTheme();
    const { hasDigitalAccountingPermission, hasClientPortalViewPermission } = useGetHomePermissions();
    const { products } = useHomeProducts({ enabled: true });
    const hasCndAvailable = useCndMenuAvailability();
    const contabilidadeDigitalProduct = products[ProductAlias.CONTABILIDADE_DIGITAL];
    const isContabilidadeDigitalActive = !contabilidadeDigitalProduct.isLoading && contabilidadeDigitalProduct.data?.status === ApiProductStatus.ACTIVE;
    const hasDigitalAccountingAccess = !!hasDigitalAccountingPermission && isContabilidadeDigitalActive;
    const isClientPortalPermissionResolved = hasClientPortalViewPermission !== undefined;
    const isLoadingClientCenterAccess = !isClientPortalPermissionResolved;
    const isNewClientCenterEnabled = !!hasClientPortalViewPermission;
    const isPermissionResolved = hasDigitalAccountingPermission !== undefined;
    const isResolvingAccess = isLoadingClientCenterAccess ||
        !isPermissionResolved ||
        (!!hasDigitalAccountingPermission && contabilidadeDigitalProduct.isLoading);
    const navItems = [
        {
            title: t('contabilidade-digital.navbar.title'),
            route: ccPaths.base,
            enabled: !isLoadingClientCenterAccess && isNewClientCenterEnabled,
        },
        {
            title: t('contabilidade-digital.navbar.title.documents'),
            route: ccPaths.documents,
            enabled: !isLoadingClientCenterAccess && isNewClientCenterEnabled,
            collapseSingleSubmenuItem: true,
            submenu: [
                { title: t('contabilidade-digital.navbar.title.published-documents'), route: ccPaths.documents },
                {
                    title: t('contabilidade-digital.navbar.title.cnd-documents'),
                    route: ccPaths.documentsCND,
                    enabled: hasCndAvailable,
                },
            ],
        },
        {
            title: t('contabilidade-digital.navbar.title.general'),
            route: ccPaths.generalRequests,
            enabled: !isLoadingClientCenterAccess && isNewClientCenterEnabled,
            submenu: [
                { title: t('contabilidade-digital.navbar.title.general-requests'), route: ccPaths.generalRequests },
                {
                    title: t('contabilidade-digital.navbar.title.general-requests.employee-registration'),
                    route: ccPaths.generalRequestsEmployeeRegistration,
                },
                {
                    title: t('contabilidade-digital.navbar.title.general-requests.taxpayers-registration'),
                    route: ccPaths.generalRequestsTaxpayersRegistration,
                },
                {
                    title: t('contabilidade-digital.navbar.title.general-requests.interns-registration'),
                    route: ccPaths.generalRequestsInternsRegistration,
                },
                {
                    title: t('contabilidade-digital.navbar.title.general-requests.vacation-notice'),
                    route: ccPaths.generalRequestsVacationNotice,
                },
                {
                    title: t('contabilidade-digital.navbar.title.general-requests.vacation-calculation'),
                    route: ccPaths.generalRequestsVacationCalculation,
                },
                {
                    title: t('contabilidade-digital.navbar.title.general-requests.termination-notice'),
                    route: ccPaths.generalRequestsTerminationNotice,
                },
                {
                    title: t('contabilidade-digital.navbar.title.general-requests.termination-calculation'),
                    route: ccPaths.generalRequestsTerminationCalculation,
                },
                {
                    title: t('contabilidade-digital.navbar.title.general-requests.employee-leave'),
                    route: ccPaths.generalRequestsEmployeeLeave,
                },
                {
                    title: t('contabilidade-digital.navbar.title.general-requests.earnings-entries'),
                    route: ccPaths.generalRequestsEarningsEntries,
                },
            ],
        },
        {
            title: t('contabilidade-digital.navbar.title.boxe'),
            route: ccPaths.boxe,
            enabled: !isLoadingClientCenterAccess && isNewClientCenterEnabled,
            submenu: [
                {
                    title: t('contabilidade-digital.navbar.submenu.boxe.documentos'),
                    route: ccPaths.boxe,
                },
                {
                    title: t('contabilidade-digital.navbar.submenu.boxe.download'),
                    route: `${ccPaths.boxe}/download`,
                },
            ],
        },
        {
            title: t('contabilidade-digital.navbar.title.invoice'),
            route: ccPaths.invoiceProcessing,
            enabled: !isLoadingClientCenterAccess && isNewClientCenterEnabled,
        },
        {
            title: t('contabilidade-digital.navbar.title.reports'),
            route: ccPaths.reports,
            enabled: !isLoadingClientCenterAccess && isNewClientCenterEnabled,
            submenu: [
                {
                    title: t('contabilidade-digital.navbar.submenu.reports.published-documents'),
                    route: ccPaths.reportsPublishedDocuments,
                },
                {
                    title: t('contabilidade-digital.navbar.submenu.reports.payroll-entries'),
                    route: ccPaths.reportsPayrollEntries,
                },
                {
                    title: t('contabilidade-digital.navbar.submenu.reports.employee-registration'),
                    route: ccPaths.reportsEmployeeRegistration,
                },
            ],
        },
        {
            title: t('contabilidade-digital.dashboard.connected-accounts'),
            route: createCdPaths().base,
            enabled: hasDigitalAccountingAccess,
        },
    ];
    const headerRightContent = _jsx(ClientCenterHeaderRightWrapper, {});
    return (_jsxs("div", { className: styles.layoutContainer, children: [_jsx(PersonalizedHeader, { children: hasTheme && headerRightContent }), _jsxs("div", { className: styles.header, children: [_jsx(NavBar, { routes: navItems }), !hasTheme && headerRightContent && _jsx("div", { className: styles.rightContent, children: headerRightContent })] }), _jsx("div", { className: styles.routedContent, children: _jsx(Suspense, { fallback: _jsx(Loading, { fullScreen: true }), children: _jsxs(Routes, { children: [!isNewClientCenterEnabled && hasDigitalAccountingAccess && (_jsx(Route, { index: true, element: _jsx(Navigate, { to: 'contas-conectadas', replace: true }) })), hasDigitalAccountingAccess && _jsx(Route, { path: 'contas-conectadas/*', element: _jsx(ContabilidadeDigital, {}) }), isNewClientCenterEnabled && (_jsx(Route, { path: '*', element: _jsx(ClientCenterAccessGuard, { children: _jsx(ClientCenterRouter, {}) }) })), !isNewClientCenterEnabled && !hasDigitalAccountingAccess && (_jsx(Route, { path: '*', element: isResolvingAccess ? _jsx(Loading, { fullScreen: true }) : _jsx(Navigate, { to: '/home', replace: true }) }))] }) }) })] }));
}
